import React, { useState, useEffect } from "react";
import { adminGetBlog, adminCreateBlog, adminUpdateBlog, adminDeleteBlog, shareBlogPost, getBlogAutoshare, setBlogAutoshare, uploadFile } from "../../api";
import { PenLine, Plus, Trash2, Loader2, Image as ImageIcon, Eye, X, ExternalLink, Facebook, Instagram, Zap } from "lucide-react";
import { useToast } from "../../hooks/use-toast";

const input = "w-full h-[44px] px-3 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f]";
const area = "w-full px-3 py-2.5 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f] resize-y";
const CATS = ["Market News", "Buyer Guide", "Landlord Guide", "Area Guide", "Company News"];
const EMPTY = { title: "", body: "", excerpt: "", coverImage: "", category: "Market News", status: "draft" };

const AdminBlog = () => {
  const { toast } = useToast();
  const [posts, setPosts] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = () => adminGetBlog().then(setPosts).catch(() => {});
  useEffect(() => { load(); getBlogAutoshare().then((d) => setAutoshare(d.enabled)).catch(() => {}); }, []);

  const [autoshare, setAutoshare] = useState(true);
  const toggleAutoshare = async () => {
    const next = !autoshare;
    setAutoshare(next);
    try { await setBlogAutoshare(next); toast({ title: next ? "Auto-share on" : "Auto-share off", description: next ? "New posts will auto-post to Facebook & Instagram." : "You'll share posts manually." }); }
    catch { setAutoshare(!next); toast({ title: "Couldn't update", variant: "destructive" }); }
  };

  const reset = () => { setForm(EMPTY); setEditingId(null); };
  const edit = (p) => { setEditingId(p.id); setForm({ title: p.title, body: p.body, excerpt: p.excerpt || "", coverImage: p.coverImage || "", category: p.category || "Market News", status: p.status || "draft" }); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const onCover = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try { const url = await uploadFile(f); setForm((s) => ({ ...s, coverImage: url })); }
    catch { toast({ title: "Upload failed", variant: "destructive" }); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const save = async (status) => {
    if (!form.title.trim() || !form.body.trim()) { toast({ title: "Title and body are required", variant: "destructive" }); return; }
    setSaving(true);
    const payload = { ...form, status, siteUrl: window.location.origin };
    try {
      if (editingId) { await adminUpdateBlog(editingId, payload); toast({ title: status === "published" ? "Post published" : "Post saved" }); }
      else { await adminCreateBlog(payload); toast({ title: status === "published" ? "Post published" : "Draft saved" }); }
      reset(); load();
    } catch (err) { toast({ title: "Couldn't save", description: err?.response?.data?.detail || "Try again.", variant: "destructive" }); }
    finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This can't be undone.`)) return;
    try { await adminDeleteBlog(p.id); setPosts((xs) => xs.filter((x) => x.id !== p.id)); if (editingId === p.id) reset(); toast({ title: "Post deleted" }); }
    catch { toast({ title: "Delete failed", variant: "destructive" }); }
  };

  const [sharing, setSharing] = useState("");
  const share = async (p, platform) => {
    setSharing(`${p.id}-${platform}`);
    try {
      const url = `${window.location.origin}/blog/${p.slug}`;
      await shareBlogPost(p.id, platform, url);
      toast({ title: `Shared to ${platform === "both" ? "Facebook + Instagram" : platform}`, description: "Your post is live on social." });
    } catch (err) {
      toast({ title: "Couldn't share", description: err?.response?.data?.detail || "Check your Meta connection.", variant: "destructive" });
    }
    setSharing("");
  };

  return (
    <div className="space-y-6" data-testid="admin-blog">
      <div className="bg-white rounded-2xl border border-[#f0ece5] p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-[20px] font-500 text-[#2b2b2b] flex items-center gap-2"><PenLine size={18} className="text-[#b8935f]" /> {editingId ? "Edit post" : "Write a new post"}</h3>
          <div className="flex items-center gap-3">
            <button onClick={toggleAutoshare} data-testid="blog-autoshare-toggle" className={`inline-flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-full border transition-colors ${autoshare ? "bg-[#b8935f] text-white border-[#b8935f]" : "border-[#e2d8c8] text-[#8a8a8a]"}`} title="Auto-post new posts to Facebook & Instagram">
              <Zap size={13} /> Auto-share {autoshare ? "on" : "off"}
            </button>
            {editingId && <button onClick={reset} className="text-[12px] text-[#8a8a8a] hover:text-[#b8935f] inline-flex items-center gap-1"><Plus size={13} /> New post</button>}
          </div>
        </div>
        <div className="space-y-3">
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Post title" className={input} data-testid="blog-title" />
          <div className="grid sm:grid-cols-2 gap-3">
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={input} data-testid="blog-category">
              {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <label className="inline-flex items-center gap-2 h-[44px] px-3 rounded-lg border border-[#e2d8c8] text-[13px] text-[#6a6a6a] hover:border-[#b8935f] cursor-pointer" data-testid="blog-cover-label">
              {uploading ? <Loader2 size={15} className="animate-spin" /> : <ImageIcon size={15} />} {form.coverImage ? "Change cover image" : "Upload cover image"}
              <input type="file" accept="image/*" hidden onChange={onCover} data-testid="blog-cover-input" />
            </label>
          </div>
          {form.coverImage && (
            <div className="relative inline-block">
              <img src={form.coverImage} alt="cover" className="h-28 rounded-lg border border-[#f0ece5] object-cover" />
              <button onClick={() => setForm({ ...form, coverImage: "" })} className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white border border-[#e2d8c8] text-red-500 grid place-items-center"><X size={13} /></button>
            </div>
          )}
          <textarea value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} rows={2} placeholder="Short summary (optional — auto-generated from the body if left blank)" className={area} data-testid="blog-excerpt" />
          <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={12} placeholder="Write your post here…" className={area} data-testid="blog-body" />
          <div className="flex flex-wrap gap-2">
            <button onClick={() => save("published")} disabled={saving} data-testid="blog-publish-btn" className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg bg-[#b8935f] text-white text-[13px] font-500 hover:bg-[#a67f4d] transition-colors disabled:opacity-60">{saving ? <Loader2 size={15} className="animate-spin" /> : <Eye size={15} />} Publish</button>
            <button onClick={() => save("draft")} disabled={saving} data-testid="blog-save-draft-btn" className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg border border-[#e2d8c8] text-[#6a6a6a] text-[13px] font-500 hover:border-[#b8935f] transition-colors disabled:opacity-60">Save as draft</button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-[#f0ece5] p-6">
        <h3 className="font-display text-[18px] font-500 text-[#2b2b2b] mb-4">All posts ({posts.length})</h3>
        {posts.length === 0 ? <p className="text-[14px] text-[#8a8a8a]">No posts yet — write your first one above.</p> : (
          <div className="space-y-2">
            {posts.map((p) => (
              <div key={p.id} className="flex items-center gap-3 p-3 rounded-lg bg-[#faf7f2] border border-[#f0ece5]" data-testid={`blog-row-${p.id}`}>
                {p.coverImage ? <img src={p.coverImage} alt="" className="w-14 h-14 rounded-lg object-cover shrink-0" /> : <div className="w-14 h-14 rounded-lg bg-[#efe7d9] grid place-items-center text-[#b8935f] shrink-0"><PenLine size={16} /></div>}
                <div className="min-w-0 flex-1">
                  <p className="text-[14px] font-500 text-[#2b2b2b] truncate">{p.title}</p>
                  <p className="text-[12px] text-[#a0a0a0]">{p.category} · {p.status === "published" ? "Published" : "Draft"}{p.publishedAt ? ` · ${new Date(p.publishedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}` : ""}</p>
                </div>
                <span className={`text-[11px] px-2 py-0.5 rounded-full shrink-0 ${p.status === "published" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{p.status}</span>
                {p.status === "published" && <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="text-[#8a8a8a] hover:text-[#b8935f] p-2" title="View"><ExternalLink size={15} /></a>}
                {p.status === "published" && (
                  <>
                    <button onClick={() => share(p, "facebook")} disabled={sharing === `${p.id}-facebook`} data-testid={`blog-share-fb-${p.id}`} title="Share to Facebook" className="text-white bg-[#1877F2] hover:opacity-90 p-2 rounded-lg disabled:opacity-50">{sharing === `${p.id}-facebook` ? <Loader2 size={14} className="animate-spin" /> : <Facebook size={14} />}</button>
                    <button onClick={() => share(p, "instagram")} disabled={sharing === `${p.id}-instagram`} data-testid={`blog-share-ig-${p.id}`} title="Share to Instagram" className="text-white bg-gradient-to-r from-[#E1306C] to-[#F77737] hover:opacity-90 p-2 rounded-lg disabled:opacity-50">{sharing === `${p.id}-instagram` ? <Loader2 size={14} className="animate-spin" /> : <Instagram size={14} />}</button>
                  </>
                )}
                <button onClick={() => edit(p)} data-testid={`blog-edit-${p.id}`} className="text-[12px] text-[#6a6a6a] hover:text-[#b8935f] px-3 py-1.5 rounded-lg border border-[#e2d8c8]">Edit</button>
                <button onClick={() => remove(p)} data-testid={`blog-delete-${p.id}`} className="text-red-500 hover:bg-red-50 p-2 rounded-lg" title="Delete"><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBlog;
